/**
 * Basketball sport plugin — implements the SportPlugin interface.
 *
 * Reason this exists: v0.6.0 introduced multi-sport aspect ratios in
 * PlaySurfaceRenderer. Positions are stored in 0-100 native space and scaled
 * by aspect.h/100. The old global SHOT_TARGETS.basketball = {x:50, y:10.5}
 * was written in half-court (100x100) space and did NOT get view-scaled
 * correctly for full court (100x188). Result: shot animation flew to
 * (50, 10.5) in half-court view (correct) but to (50, 10.5) → scaled to
 * (50, 19.74) in full-court view (~11% down from the top — nowhere near
 * the actual hoop rendered around y=8).
 *
 * This plugin owns the coord math so PlaybookStudio just asks the plugin
 * where the target is for the current view / attack end.
 */

import config from './config';
import {
  renderPathItem as _renderPathItem,
  renderPassItem as _renderPassItem,
  renderScreenItem as _renderScreenItem,
  renderShotItem as _renderShotItem,
} from './renderers';

// HOOP_POSITIONS in the sport's native coordinate space. Every entry is
// (x, y) that PlaybookStudio will scale by scaleY (== aspect.h / 100) when
// rendering. Storing values in native pre-scale space keeps the scaling
// pipeline unchanged.
export const HOOP_POSITIONS = {
  half: { x: 50, y: 10.5 },        // half-court render: single hoop at top
  fullTop: { x: 50, y: 4.5 },      // full court: attacking the top basket (y ≈ 4.5 pre-scale → ~8.5 post-scale on 188-tall surface)
  fullBottom: { x: 50, y: 95.5 },  // full court: attacking the bottom basket (y ≈ 95.5 pre-scale → ~179.5 post-scale)
};

/**
 * @param {'half'|'full'} view
 * @param {'top'|'bottom'} [attackEnd='top']  Which basket the offense is attacking
 * @returns {{x:number, y:number}}
 */
export const getShotTarget = (view, attackEnd = 'top') => {
  if (view === 'half') return HOOP_POSITIONS.half;
  return attackEnd === 'bottom' ? HOOP_POSITIONS.fullBottom : HOOP_POSITIONS.fullTop;
};

const basketballPlugin = {
  id: 'basketball',
  name: 'Basketball',
  config,
  getShotTarget,
  ballEmoji: '\u{1F3C0}',
};

export default basketballPlugin;

// Named exports — import from renderers happens at top of file, we just alias-re-export here
export const renderPathItem = _renderPathItem;
export const renderPassItem = _renderPassItem;
export const renderScreenItem = _renderScreenItem;
export const renderShotItem = _renderShotItem;
